<template>
  <section class="schedule-appointment">
    <appointment-month-side-bar
      initial-category="employees"
      v-bind:cabinet-list="cabinets"
      v-bind:employee-list="doctors"
      v-bind:initial-date="day"
      v-bind:initial-employee-id="employeeId"
      v-bind:is-collapsed="isCollapsedSideBar"
      v-on:collapse="onSidebarCollapsed"
    />
    <div class="schedule-appointment__wrap">
      <appointment-schedule-head
        v-bind:date-title="dateTitle"
        v-bind:initial-date="day"
        v-on:collapse="onSidebarCollapsed(true)"
        v-on:open-day="onOpenDay"
      />
      <div
        class="schedule-appointment__month__body"
        ref="scheduleAppointmentTable">
        <div class="schedule-appointment__month__row">
          <div class="schedule-appointment__month__day-week">ПН</div>
          <div class="schedule-appointment__month__day-week">ВТ</div>
          <div class="schedule-appointment__month__day-week">СР</div>
          <div class="schedule-appointment__month__day-week">ЧТ</div>
          <div class="schedule-appointment__month__day-week">ПТ</div>
          <div class="schedule-appointment__month__day-week off">СБ</div>
          <div class="schedule-appointment__month__day-week off">ВС</div>
        </div>
        <p v-if="!hasWorkDays">Рабочих дней нет</p>
        <article
          class="schedule-appointment__month__row"
          v-for="(week, wIndex) in weeks"
          v-bind:key="wIndex">
          <button
            type="button"
            class="schedule-appointment__month__day"
            v-bind:class="[...day.tags, isSelected(day.date)]"
            v-bind:disabled="day.tags.includes('filler')"
            v-bind:key="day.date"
            v-for="day in week"
            v-on:click="onSelectDay(day)">
            <div class="schedule-appointment__month__day__body">
              <div class="schedule-appointment__month__day__number">{{day.title}}</div>
              <div
                class="schedule-appointment__month__day__slot"
                v-for="(slot, index) in day.slots"
                v-bind:class="slot.tags"
                v-bind:style="getSlotCSS(slot)"
                v-bind:key="index" />
            </div>
          </button>
        </article>
      </div>
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        label="Редактирование перерыва"
        v-bind:need-confirm="needConfirmBreakEditForm"
        v-if="wasSelectedBreakSlot"
        v-on:cancel="selectedBreak = null">
        <form-employee-break-edit
          v-bind:allowed-dates="employeeBreakAllowedDates"
          v-bind:break-id="selectedBreak.id"
          v-bind:employee="selectedBreak.employee"
          v-bind:initial-date="selectedBreak.date"
          v-bind:initial-ends-time="selectedBreak.endsTime"
          v-bind:initial-note="selectedBreak.note"
          v-bind:initial-starts-time="selectedBreak.startsTime"
          v-bind:input-errors="breakInputErrors"
          v-bind:timeline="employeeBreakTimeline"
          v-on:cancel-break="onCancelBreak"
          v-on:cancel-form="selectedBreak = null"
          v-on:change-values="onChangeFieldValues"
          v-on:reload-allowed-dates="onReloadEmployeeBreakAllowedDates"
          v-on:reload-timeline="onReloadEmployeeBreakTimeline"
          v-on:store-break="onEditBreak"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-else-if="isShowAppointmentForm"
        v-bind:label="dialogAppointmentTitle"
        v-bind:need-confirm="needConfirmAppointmentForm"
        v-on:cancel="onCloseAppointmentForm"
        class="side-absolute">
        <form-appointment-creation
          has-copy
          method="dialog"
          v-bind:is-selected-appointments="wasSelectedAppointment"
          v-bind:advertising-channel-list="advertisingChannels"
          v-bind:rejection-reason-list="rejectionReasons"
          v-bind:can-create-patients="canCreatePatients"
          v-bind:can-edit-initial="canEditInitial"
          v-bind:employee-list="appointmentWorkEmployees"
          v-bind:employee-timeline="appointmentEmployeeTimeline"
          v-bind:employee-work-days="appointmentEmployeeWorkDays"
          v-bind:favorite-services="servicesWithFavorites.favorites"
          v-bind:initial-date="appointmentDate"
          v-bind:initial-employee-id="appointmentEmployeeId"
          v-bind:initial-month="appointmentMonth"
          v-bind:suggested-patient-id="appointmentPatientId"
          v-bind:initial-time-ends="appointmentTimeEnds"
          v-bind:initial-time-starts="appointmentTimeStarts"
          v-bind:input-errors="appointmentInputErrors"
          v-bind:is-await-request="isAppointmentAwaitRequest"
          v-bind:is-outdated="appointmentIsOutdated"
          v-bind:matched-patients="appointmentMatchedPatients"
          v-bind:patient-list="patients"
          v-bind:service-list="servicesWithFavorites.all"
          v-bind="appointmentProps"
          v-bind:has-view-patient-medical-card="hasViewPatientMedicalCard"
          v-bind:has-view-patient-visit="hasViewPatientVisit"
          v-bind:has-view-patient-invoices="hasViewPatientInvoices"
          v-on:came="onCameAppointment"
          v-on:cancel="onCancelAppointment"
          v-on:change-values="onChangeFieldValues"
          v-on:clear-matched-patients="onClearMatchedPatients"
          v-on:close="onCloseAppointmentForm"
          v-on:confirm="onConfirmAppointment"
          v-on:copy="onCopy"
          v-on:create-advertising-channel="onCreateAdvertisingChannel"
          v-on:input-date="onInputAppointmentDate"
          v-on:input-employee="onInputAppointmentEmployee"
          v-on:input-month="onInputAppointmentMonth"
          v-on:input-times="onInputAppointmentTimes"
          v-on:match-patients="onMatchedPatients"
          v-on:open-visit="onOpenVisit"
          v-on:open-waitings="openWaitingList"
          v-on:reload-timeline="onReloadEmployeeDayTimeline"
          v-on:submit-creating="onSubmitAppointmentNewPatient"
          v-on:submit-editing="onSubmitAppointmentExistsPatient"
        />
      </confirm-side-dialog>
    </side-slide-transition>
    <side-slide-transition>
      <timetable-employee-day
        v-bind:date-title="selectedDay.dateTitle"
        v-bind:employee="employee"
        v-bind:has-edit-breaks="hasEditBreaks"
        v-bind:iso-date="selectedDay.date"
        v-bind:slots="selectedDay.slots"
        v-bind:tags="selectedDay.tags"
        v-if="wasSelectedDay"
        v-on:open-appointment="openAppointment"
        v-on:open-break="openFormEmployeeBreakEdit"
        v-on:open-context-menu="onOpenContextMenu" />
    </side-slide-transition>
    <iframe
      class="schedule-appointment__print"
      ref="printView"
      title="printView"
    />
    <context-menu-appointment-slot
      v-if="!disableContextMenu"
      v-bind:attach="contextMenuTarget"
      v-bind:rejection-reason-list="rejectionReasons"
      v-bind:isCopyAppointmentAction="isCopyAppointmentAction"
      v-bind:isPasteAppointmentAction="isPasteAppointmentAction"
      v-bind:disabledPasteAppointmentAction="disabledPasteAppointmentAction"
      v-bind:isChangeStatusAvailable="isChangeStatusAvailable"
      v-bind:isGoToPatientMedicalCard="isGoToPatientMedicalCard"
      v-bind:isPrintFutureAppointments="isPrintFutureAppointments"
      v-bind:isShowCancelModal="isShowCancelModal"
      v-bind:input-errors="changeStatusInputErrors"
      v-bind:current-status="currentStatus"
      v-bind:position-x="offsetContextMenuX"
      v-bind:position-y="offsetContextMenuY"
      v-model="isShowSlotContextMenu"
      v-on:copy="onCopyAppointment"
      v-on:paste="onPasteAppointmentCopy"
      v-on:change-appointment-status="changeAppointmentStatus"
      v-on:transfer-to-medcard="onPatientMedicalCard"
      v-on:print-future-appointments="onPrintFutureAppointments"
      v-on:show-cancel-dialog="onShowCancelDialog"
    />
  </section>
</template>
<script>
import appointmentMixin, { AppointmentSlot } from '@/mixins/appointmentMixin';
import dateMixin from '@/mixins/dateMixin';
import notifierMixin from '@/mixins/notifierMixin';
import FormAppointmentCreation from '@/components/FormAppointmentCreation.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import AppointmentMonthSideBar from '@/components/AppointmentMonthSideBar.vue';
import TimetableEmployeeDay from '@/components/TimetableEmployeeDay.vue';
import employeeBreakMixin from '@/mixins/employeeBreakMixin';
import FormEmployeeBreakEdit from '@/components/FormEmployeeBreakEdit.vue';
import ContextMenuAppointmentSlot from '@/components/ContextMenuAppointmentSlot.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import AppointmentScheduleHead from '@/components/AppointmentScheduleHead.vue';

const AppointmentForm = FormAppointmentCreation.name;
const BreakEditForm = FormEmployeeBreakEdit.name;

const needConfirm = (form) => ({ needConfirmBeforeClosed }) => needConfirmBeforeClosed[form];

/**
 * @description Компонент страницы графика приемов.
 * @type {Vue}
 */
export default {
  components: {
    FormAppointmentCreation,
    SideSlideTransition,
    AppointmentMonthSideBar,
    TimetableEmployeeDay,
    FormEmployeeBreakEdit,
    ContextMenuAppointmentSlot,
    ConfirmSideDialog,
    AppointmentScheduleHead,
  },
  computed: {
    /**
     * @description Может создавать пациентов.
     * @type {boolean}
     */
    hasCreatePatients() {
      if (this.selectedDay === null) {
        return false;
      }
      return this.selectedDay.actions.includes('create-patients');
    },
    /**
     * @description Выбранный день в формате ISO.
     * @type {string}
     */
    isoDate: ({ day, formatDateToISO }) => formatDateToISO(day),
    /**
     * @description Шаг по умолчанию.
     * @type {number}
     */
    defaultTimeStep({ wasSelectedDay, selectedDay }) {
      if (wasSelectedDay) {
        return selectedDay.defaultTimeStep;
      }
      return 0;
    },
    /**
     * @description Заголовок даты.
     * @type {string}
     */
    dateTitle() {
      return this.wasSelectedDay
        ? this.selectedDay.dateTitle
        : '';
    },
    /**
     * @description Идентификатор сотрудника.
     * @type {number|string}
     */
    employeeId({ $route, $options }) {
      const value = $route.params.id;
      if (value === $options.allEmployeeValue) {
        return value;
      }
      return parseInt(value, 10);
    },
    /**
     * @description Наличие рабочих дней в месяце.
     * @type {boolean}
     */
    hasWorkDays: ({ weeks, isAwaitReloadTimetable }) => isAwaitReloadTimetable || weeks.length > 0,
    /**
     * @description Выбранный период.
     * @type {string}
     */
    period: () => 'month',
    /**
     * @description Выбран день для просмотра расписания на день.
     * @type {boolean}
     */
    wasSelectedDay: ({ selectedDay }) => !!selectedDay,
    /**
     * @description Год и месяц.
     * @type {string}
     */
    yearAndMonth: ({ $route }) => $route.params.month,
    /**
     * @description Редактировать перерывы сотрудников.
     * @type {any}
     */
    hasEditBreaks() {
      if (!this.wasSelectedDay) {
        return false;
      }
      return this.selectedDay.actions.includes('breaks');
    },
    needConfirmAppointmentForm: needConfirm(AppointmentForm),
    needConfirmBreakEditForm: needConfirm(BreakEditForm),
  },
  AppointmentForm,
  BreakEditForm,
  allEmployeeValue: 'all',
  created() {
    this.reloadTimetable();
  },
  data() {
    const paramsDay = this.$route.params.day;
    const day = paramsDay ? new Date(paramsDay) : new Date(this.$route.params.month);
    const needConfirmBeforeClosed = {};
    needConfirmBeforeClosed[AppointmentForm] = false;
    needConfirmBeforeClosed[BreakEditForm] = false;
    return {
      /**
       * @description Выбранный день.
       * @type {Date}
       */
      day,
      /**
       * @description Список кабинетов.
       * @type {({color: string, id: string, name: string}[])}
       */
      cabinets: this.initialCabinets,
      /**
       * @description Сотрудник.
       * @type {any}
       */
      employee: {
        id: 0,
        name: '',
        positions: '',
      },
      /**
       * @description Выбранный день.
       * @type {any}
       */
      selectedDay: null,
      /**
       * @description Недели.
       * @type {any[]}
       */
      weeks: [],
      /**
       * @description Необходимо подтверждение перед закрытием формы.
       * @type {any}
       */
      needConfirmBeforeClosed,
    };
  },
  methods: {
    /**
     * @description Обработать открытие расписания на день.
     * @param {Date} day День.
     * @returns {void}
     */
    onOpenDay(day) {
      this.day = day;
      this.onSelectDay({ date: this.isoDate, tags: [] });
    },
    isSelected(day) {
      if (this.wasSelectedDay) {
        return this.selectedDay.date === day ? 'selected' : '';
      }
      return '';
    },
    /**
     * @description Получить список записей на прием.
     * @param {string} date Дата.
     * @returns {any[]}
     */
    async fetchAppointmentList(date) {
      const result = await this.$appointmentScheduleGateway.viewEmployeeAppointmentsDay(
        date,
        this.employeeId,
      );
      return result.fold(
        () => ({ actions: [], slots: [], defaultTimeStep: 0 }),
        (data) => ({
          actions: data.actions,
          slots: data.data,
          defaultTimeStep: data.default_time_step,
        }),
      );
    },
    /**
     * @description Получить стили для слота.
     * @param {any} slot
     * @returns {any}
     */
    getSlotCSS(slot) {
      const height = slot.ratio_to_step * 100;
      return {
        height: `${height}%`,
      };
    },
    /**
     * @description Обработать выбор дня.
     * @param {{date:string, tags:string[]}}
     * @returns {void}
     */
    async onSelectDay({ date, tags }) {
      this.day = new Date(date);
      const selectedDay = {
        actions: [],
        slots: [],
        date,
        dateTitle: this.getShortDate(date),
        employeeName: this.employee.name,
        defaultTimeStep: 0,
        tags,
      };
      if (!tags.includes('off')) {
        const result = await this.fetchAppointmentList(date);
        selectedDay.actions = result.actions;
        selectedDay.slots = result.slots;
        selectedDay.defaultTimeStep = result.defaultTimeStep;
      }
      this.selectedDay = selectedDay;
      this.onCloseAppointmentForm();
    },
    /**
     * @description Обработать отмену перерыва сотрудника.
     * @param {number} breakId Идентификатор перерыва сотрудника.
     * @returns {void}
     */
    async onCancelBreak(breakId) {
      const result = await this.sendCancelBreakRequest(breakId);
      result.forEach(() => this.reloadTimetable(this.viewQuery));
    },
    /**
     * @description Скопировать запись на прием.
     * @returns {void}
     */
    onCopyAppointment() {
      this.appointmentSlotTarget.forEach((slot) => {
        this.copyAppointmentById(
          slot.getAppointmentId(),
          slot.getEmployeeId(),
        );
      });
    },
    /**
     * @description Обработать редактирование перерыва сотрудника.
     * @param {EditEmployeeBreakRequest} request Запрос.
     * @returns {void}
     */
    async onEditBreak(request) {
      const result = await this.sendEditBreakRequest(request);
      result.forEach(() => this.reloadTimetable(this.viewQuery));
    },
    /**
     * @description Обработать показ контекстного меню.
     * @param {{
     *  offset:any,
     *  currentStatus:string,
     *  target:Element,
     *  slot:any
     * }} event Событие.
     */
    onOpenContextMenu({
      offset,
      currentStatus,
      slot,
      target,
    }) {
      const actions = slot.actions || [];
      const appointmentSlot = new AppointmentSlot(
        actions,
        slot.tags,
        this.selectedDay.date,
        slot.starts_at,
        slot.ends_at,
        slot.id || null,
        this.employee.id,
        null,
        slot.patient_id || null,
        slot.has_next_appointments,
      );
      if (appointmentSlot.isOpenSlot() || appointmentSlot.isPatientSlot()) {
        this.openAppointmentSlotContextMenu(offset, currentStatus, appointmentSlot, target);
      }
    },
    /**
     * @description Обработать вставку скопированной записи на прием.
     * @returns {void}
     */
    onPasteAppointmentCopy() {
      this.appointmentSlotTarget.forEach(this.onPasteAppointment);
    },
    /**
     * @description Открыть запись на прием.
     * @param {any} appointment Запись на прием.
     * @returns {void}
     */
    openAppointment(appointment) {
      this.openAppointmentDialog(
        appointment.id,
        this.selectedDay.date,
        {
          id: this.employeeId,
          name: this.selectedDay.employeeName,
        },
        appointment.ends_at,
        appointment.starts_at,
      );
    },
    /**
     * @description Перезагрузить данные таблицы.
     * @returns {void}
     */
    async reloadTimetable() {
      if (this.wasSelectedDay) {
        const onResponse = (result) => {
          this.selectedDay.actions = result.actions;
          this.selectedDay.slots = result.slots;
          this.selectedDay.defaultTimeStep = result.defaultTimeStep;
        };
        this.fetchAppointmentList(this.selectedDay.date).then(onResponse);
      }
      if (this.employeeId === this.$options.allEmployeeValue) {
        this.weeks = [];
        this.employee = {
          id: 0,
          name: '',
          positions: '',
        };
        return;
      }
      this.isAwaitReloadTimetable = true;
      try {
        this.onCloseAppointmentForm();
        const result = await this.$appointmentScheduleGateway.getViewByMonth(
          this.employeeId,
          this.yearAndMonth,
        );
        result.forEach((data) => {
          this.actions = data.actions || [];
          this.cabinets = data.cabinets;
          this.weeks = data.weeks;
          this.employee = data.employee;
          if (!this.wasSelectedDay) {
            const date = data.day_for_show_appointments;
            if (date) {
              this.onSelectDay({ date, tags: [] });
            }
          }
        });
      } finally {
        this.isAwaitReloadTimetable = false;
      }
    },
  },
  props: {
    initialCabinets: { type: Array, default: () => [] },
  },
  mixins: [
    appointmentMixin,
    dateMixin,
    employeeBreakMixin,
    notifierMixin,
  ],
  name: 'ClinicPanelAppointmentScheduleEmployeeMonth',
};
</script>
<style lang="scss" scoped>
$button-font-size: 14px;
$button-height: 33px;
$button-width: 100%;
.only-print {
  display: none;
}
.schedule-appointment {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: inherit;
  overflow: hidden;
  &__wrap {
    width: 100%;
    height: 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
  &__body {
    display: flex;
    flex-direction: row;
    height: fit-content;
    width: fit-content;
    overflow: hidden;
    position: relative;
  }
  &__timetable {
    overflow-x: scroll;
    &__grid {
      padding: 15px 20px 0 20px;
      display: flex;
      flex-direction: row;
      grid-gap: 16px;
      &:not(.await):empty::before {
        content: attr(msg);
        font-size: 20px;
        line-height: 25px;
        font-weight: 600;
        margin: 45px;
      }
    }
    &__column {
      display: flex;
      flex-direction: column;
      grid-gap: 8px;
      width: 280px;
      max-width: 280px;
      min-width: 280px;
    }
  }
  &__month {
    &__body {
      flex-direction: column;
      display: flex;
      width: 100%;
      overflow: hidden;
      position: relative;
    }
    &__row {
      display: grid;
      grid-template-columns: repeat(7, auto);
    }
    &__day-week {
      width: 100%;
      font-size: 12px;
      font-weight: 600;
      line-height: 14px;
      padding: 8.5px 0;
      text-align: center;
      text-transform: uppercase;
      color: #707683;
      border: 1px solid #eaeff3;
      &.off {
        color: #ef474c;
      }
    }
    &__day {
      border: 1px solid #c2cfe0;
      background-color: #eaeff3;
      padding-bottom: 100%;
      position: relative;
      &.off {
        background: rgba(234, 239, 243, 0.4);
        .schedule-appointment__month__day__number {
          color: rgba(239, 71, 76, 0.6);
        }
      }
      &.selected {
        transition: all var(--animate-duration);
        border-color: var(--action-color);
        background: rgba(194, 207, 224, 0.7);
        .schedule-appointment__month__day__number {
          transition: color var(--animate-duration);
          color: var(--action-color);
        }
      }
      &__number {
        font-weight: 400;
        font-size: 16px;
        line-height: 19px;
        align-self: flex-end;
        margin: 4px 9px 0;
        padding: 2.5px;
        align-items: center;
        text-align: center;
        text-transform: uppercase;
        color: #c2cfe0;
      }
      &__body {
        display: flex;
        flex-direction: column;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
      &__slot {
        width: 100%;
        &.empty {
          background-color: transparent;
        }
        &.patient {
          background-color: #fac425;
        }
        &.confirm {
          background-color: #abd576;
        }
        &.break {
          background-color: var(--unfocus-color);
        }
      }
      &.work {
        background-color: #eaeff3;
      }
      &.filler {
        background-color: white;
        border: 1px solid #eaeff3;
      }
    }
    &__column {
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      width: 160px;
    }
  }
  &__print {
    display: none;
  }
}
@media print {
  .only-print {
    display: block;
  }
  .schedule-appointment__side-bar {
    display: none !important;
  }
  .schedule-appointment__wrap {
    padding: 20px;
  }
  .head-panel {
    display: none;
  }
  .clinic-panel {
    width: fit-content;
    height: fit-content;
    min-height: fit-content;
  }
  .schedule-appointment__timetable__cell,
  .schedule-appointment__timetable__employee__print {
    break-inside: avoid;
  }
  .schedule-appointment__side-bar__row {
    display: none;
  }
}
@media screen and (max-width: 480px) {
  .schedule-appointment {
    flex-direction: column;
    overflow-y: auto;
    &__wrap {
      height: fit-content;
      overflow: hidden;
      flex: 1 0 auto;
    }
  }
}
</style>
